import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { type SetupIntentResult } from '@stripe/stripe-js';
import { type StripePaymentElementChangeEvent } from '@stripe/stripe-js';

import {
  Button,
  DisplayText,
  IllusEmpty_2,
  Image,
  LoadingPlaceholder,
  theme,
  useToggleState,
} from '@sg/garnish';

import { getEnvVars } from '@order/utils';

import { useCustomerSession } from '../../hooks';
import { loadStripeWithRetry } from '../../utils';
import { useSetDefaultPaymentMethodMutation } from './GraphQL/DefaultPaymentMethod.generated';

export const StripeCustomerSheet = (props: StripeCustomerSheetProps) => {
  const { redirectTo = defaultRedirect, onClose } = props;
  const { value: isReady, toggleOn: setIsReady } = useToggleState();

  const {
    setupIntentSecret: clientSecret,
    customerSessionClientSecret,
    isFetching: isFetchingCustomerSession,
    hasError: hasCustomerSessionError,
  } = useCustomerSession();

  return (
    <View>
      {!isReady && !hasCustomerSessionError ? (
        <LoadingPlaceholder rows={4} rowHeight={[48, 48, 48, 48]} palette="darkGray" />
      ) : null}

      {hasCustomerSessionError ? (
        <View style={styles.container}>
          <Image source={IllusEmpty_2} style={styles.illustration} />

          <DisplayText sizeMatch={['24']} style={styles.title}>
            <FormattedMessage {...messages.error} />
          </DisplayText>
        </View>
      ) : null}

      {isFetchingCustomerSession || hasCustomerSessionError ? null : (
        <Elements stripe={stripePromise} options={{ clientSecret, customerSessionClientSecret }}>
          <View style={isReady ? null : styles.hiddenContainer}>
            <LoadedForm redirectTo={redirectTo} onClose={onClose} setIsReady={setIsReady} />
          </View>
        </Elements>
      )}
    </View>
  );
};

const LoadedForm = (props: LoadedFormProps) => {
  const { redirectTo, onClose, setIsReady } = props;

  const stripe = useStripe();
  const elements = useElements();

  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();

  // ─── Callbacks ───────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: redirectTo },
      redirect: 'if_required',
    });

    const paymentMethodId = getPaymentMethodId(result);

    if (paymentMethodId) {
      await setDefaultPaymentMethod({ paymentMethodId });
    }

    setIsSubmitting(false);
    onClose();
  }, [stripe, elements, redirectTo, setDefaultPaymentMethod, onClose]);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) return;

    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
      const isCard = event.value.type === 'card';
      const isComplete = event.complete;
      const paymentMethodId = event.value.payment_method?.id;

      // Customer selected a payment method, set it as the default.
      if (paymentMethodId) setDefaultPaymentMethod({ paymentMethodId });

      // Customer filled out the form, allow submission.
      setCanSubmit(isCard && isComplete && !paymentMethodId);
    };

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, setDefaultPaymentMethod]);

  return (
    <View style={styles.container}>
      <PaymentElement onReady={setIsReady} />

      {canSubmit ? (
        <Button palette="primary" size="large-wide" isLoading={isSubmitting} onPress={handleSubmit}>
          <FormattedMessage {...messages.save} />
        </Button>
      ) : null}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Stripe Customer Sheet | Error title',
  },
  save: {
    defaultMessage: 'Save card',
    description: 'Stripe Customer Sheet | Save CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: theme.spacing['6'],
    paddingBottom: theme.spacing['4'],
  },
  illustration: {
    width: 150,
    height: 150,
    alignSelf: 'center',
  },
  title: {
    textAlign: 'center',
  },
  hiddenContainer: {
    display: 'none',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getPaymentMethodId = (result: SetupIntentResult) => {
  if (typeof result.setupIntent?.payment_method === 'string') {
    return result.setupIntent?.payment_method;
  }

  return result.setupIntent?.payment_method?.id;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type StripeCustomerSheetProps = {
  redirectTo?: string;
  visible: boolean;
  onClose: () => void;
};

type LoadedFormProps = {
  redirectTo: string;
  onClose: () => void;
  setIsReady: () => void;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY;
const stripePromise = loadStripeWithRetry(stripePublicKey);
const defaultRedirect = `https://${window.location.host}`;
